// Пассивные свойства карт, жетонов свойств и ЖДК — и точки, где ядро движка их спрашивает.
// Ядро не знает ни одной карты по имени: оно сворачивает ответы всех подходящих пассивов.
// Сами пассивы объявлены рядом со скриптами карт в tokens.ts (PASSIVES, TRAITS, DDK_PASSIVES).
import type { CardDef, CardInst, DdkDef, Game, Player, PromptOption, ScoreLine, TokenActivation, TraitDef } from '../types.ts';
import { card, ddkDef, gainChips, hasTag, isWand, log, traitOf } from './state.ts';
import { bossPassive, isJunk } from './bosses.ts';
import type { Eff, EffectCtx } from './effects.ts';
import * as T from './tokens.ts';

/** Контекст подсчёта очков в конце игры */
export interface EndGameCtx {
  /** все карты игрока */
  owned: CardInst[];
  /** вялых палочек среди них */
  limpCount: number;
  /** вялые палочки приносят ПО, а не отнимают (Виагрус) */
  limpPositive: boolean;
  /** легенд «для владельца» (с учётом жетона «фамильяры — легенды») */
  legends: number;
}

/** Пассивное свойство карты. Действует, пока карта — постоянка под контролем (кроме endGameVP — по владению). */
export interface CardPassive {
  /** модификатор урона, который наносит владелец */
  modifyDamage?(G: Game, ctx: EffectCtx, target: Player, n: number, self: CardInst): number;
  /** владелец нанёс урон */
  afterDamage?(G: Game, ctx: EffectCtx, target: Player, n: number, self: CardInst): void;
  /** владелец завершил атаку */
  afterAttack?(G: Game, ctx: EffectCtx, self: CardInst): void;
  /** владелец сыграл карту */
  onPlay?(G: Game, p: Player, played: CardInst, self: CardInst): void;
  /** мощь в начале хода */
  startPower?(G: Game, p: Player, self: CardInst): number;
  startOfTurn?(G: Game, p: Player, self: CardInst): Eff;
  /** добавка к размеру руки */
  handLimit?(G: Game, p: Player, self: CardInst): number;
  /** куда владелец может положить полученную карту (кроме сброса) */
  gainDestinations?(G: Game, p: Player, gained: CardInst, fromMarket: boolean, self: CardInst): PromptOption[];
  /** вялые палочки владельца приносят ПО вместо штрафа */
  limpPositive?: boolean;
  /** карта относится ко всем типам (тег allTypes) — учитывается в typeMatches */
  /** эта карта получена игроком (срабатывает на самой карте) */
  onGained?(G: Game, p: Player, self: CardInst): void;
  /** строка очков в конце игры по всем картам владельца */
  endGameVP?(G: Game, p: Player, ctx: EndGameCtx, self: CardInst): ScoreLine | null;
}

/** Пассивное свойство жетона колдунского свойства */
export interface TraitPassive {
  /** стартовая колода (по умолчанию STARTER_DECK) */
  starterDeck?(mk: (cid: string) => CardInst): CardInst[];
  /** жизни в начале и после смерти (без лошары); функция — чтобы правка режима ребаланса видела G */
  life?(G: Game): number;
  /** владелец получает главный приз в начале партии */
  startsWithPrize?: boolean;
  /** …кроме партий, где ветка режима это отменяет (версия 8: приз без стартового приза) */
  startsWithPrizeIn?(G: Game): boolean;
  /** фамильяры считаются легендами (скидки, чипсины, подсчёт) */
  familiarsAreLegends?: boolean;
  /** третий (ничейный) фамильяр при подготовке */
  threeFamiliars?: boolean;
  /** скидка на карту барахолки */
  discount?(def: CardDef): number;
  modifyDamage?(G: Game, ctx: EffectCtx, target: Player, n: number): number;
  /** атаки палочками нельзя избежать */
  wandsUnavoidable?: boolean;
  onPlay?(G: Game, p: Player, played: CardInst): void;
  onGain?(G: Game, p: Player, gained: CardInst): Eff;
  gainDestinations?(G: Game, p: Player, gained: CardInst, fromMarket: boolean): PromptOption[];
  /** доп. добор в конце хода */
  extraDraw?(G: Game, p: Player): number;
  activation?: TokenActivationDef;
  endGameVP?(G: Game, p: Player, ctx: EndGameCtx): ScoreLine | null;
}

/** Активация ☀ жетона: условие, подсказка, эффект; auto — срабатывает сама, как только условие выполнено */
export interface TokenActivationDef {
  id: string;
  label: string;
  condition(G: Game, p: Player): boolean;
  hint: string;
  run(G: Game, p: Player): Eff;
  auto?: boolean;
}

/** Пассивное свойство жетона дохлого колдуна (по ключу текста) */
export interface DdkPassive {
  modifyDamage?(G: Game, ctx: EffectCtx, target: Player, n: number): number;
  /** владелец не получает чипсину за главный приз */
  noPrizeBonus?: boolean;
  /** в конце игры жетон уничтожается (и не считается) */
  destroyedAtEnd?(G: Game, p: Player, self: DdkDef): boolean;
  endGameVP?(G: Game, p: Player, ctx: EndGameCtx, self: DdkDef): ScoreLine | null;
  activation?: TokenActivationDef;
}

// ---------- выборка пассивов ----------

function permanentPassives(p: Player): { c: CardInst; ps: CardPassive }[] {
  const res: { c: CardInst; ps: CardPassive }[] = [];
  for (const c of p.permanents) {
    const ps = T.PASSIVES[c.cid];
    if (ps) res.push({ c, ps });
  }
  return res;
}

function trait(p: Player): { def: TraitDef; ps: TraitPassive } | null {
  const def = traitOf(p);
  if (!def) return null;
  const ps = T.TRAITS[def.key];
  return ps ? { def, ps } : null;
}

function ddkPassives(p: Player): { def: DdkDef; ps: DdkPassive }[] {
  const res: { def: DdkDef; ps: DdkPassive }[] = [];
  for (const id of p.ddk) {
    const def = ddkDef(id);
    const ps = T.DDK_PASSIVES[def.key];
    if (ps) res.push({ def, ps });
  }
  return res;
}

export const traitPassive = (p: Player): TraitPassive | null => trait(p)?.ps ?? null;

// ---------- точки вызова ----------

/** Порядок важен: сначала добавки (жетоны), потом карты (среди них есть множители) */
export function modifyDamage(G: Game, ctx: EffectCtx, target: Player, n: number): number {
  const src = ctx.source;
  if (!src) return n;
  for (const { ps } of ddkPassives(src)) if (ps.modifyDamage) n = ps.modifyDamage(G, ctx, target, n);
  const t = traitPassive(src);
  if (t?.modifyDamage) n = t.modifyDamage(G, ctx, target, n);
  const b = bossPassive(src);
  if (b?.wandDamageBonus && ctx.card && isWand(ctx.card)) n += b.wandDamageBonus;
  if (b?.attackDamageBonus && ctx.isAttack) n += b.attackDamageBonus;
  for (const { c, ps } of permanentPassives(src)) if (ps.modifyDamage) n = ps.modifyDamage(G, ctx, target, n, c);
  return n;
}

export function afterDamage(G: Game, ctx: EffectCtx, target: Player, n: number): void {
  const src = ctx.source;
  if (!src) return;
  const b = bossPassive(src);
  if (b?.chipsOnHit && ctx.isAttack && n > 0 && target.id !== src.id && G.turn && G.turn.player === src.id && !G.turn.bossHit) {
    G.turn.bossHit = true;
    gainChips(G, src, b.chipsOnHit, 'босс');
  }
  for (const { c, ps } of permanentPassives(src)) ps.afterDamage?.(G, ctx, target, n, c);
}

export function afterAttack(G: Game, ctx: EffectCtx): void {
  const src = ctx.source;
  if (!src) return;
  for (const { c, ps } of permanentPassives(src)) ps.afterAttack?.(G, ctx, c);
}

export function wandsUnavoidable(p: Player): boolean {
  return !!traitPassive(p)?.wandsUnavoidable;
}

/** Босс, чьи атаки нельзя избежать (Бензорук) */
export function allAttacksUnavoidable(p: Player): boolean {
  return !!bossPassive(p)?.allAttacksUnavoidable;
}

export function onPlay(G: Game, p: Player, played: CardInst): void {
  traitPassive(p)?.onPlay?.(G, p, played);
  for (const { c, ps } of permanentPassives(p)) ps.onPlay?.(G, p, played, c);
}

export function startPower(G: Game, p: Player): number {
  let n = 0;
  for (const { c, ps } of permanentPassives(p)) if (ps.startPower) n += ps.startPower(G, p, c);
  return n;
}

export function* startOfTurn(G: Game, p: Player): Eff {
  for (const { c, ps } of permanentPassives(p)) if (ps.startOfTurn) yield* ps.startOfTurn(G, p, c);
}

export function handLimit(G: Game, p: Player, base: number): number {
  let n = base + (bossPassive(p)?.handBonus ?? 0);
  for (const { c, ps } of permanentPassives(p)) if (ps.handLimit) n += ps.handLimit(G, p, c);
  return n;
}

/** Пассивка босса в начале его хода: чипсины, хлам с верха колоды в сброс, неизбежная первая атака */
export function bossStartOfTurn(G: Game, p: Player): void {
  const b = bossPassive(p);
  if (!b) return;
  if (b.chipsPerTurn && (b.chipsCap == null || p.chips < b.chipsCap)) gainChips(G, p, b.chipsPerTurn, 'босс');
  if (b.trashTopJunk) {
    const top = p.deck[p.deck.length - 1];
    if (top && isJunk(card(top), !!b.trashSigns)) {
      p.deck.pop();
      p.discard.push(top);
      log(G, `${p.name}: споры съедают «${card(top).name}» с верха колоды`);
    }
  }
  if (b.firstAttackUnavoidable && G.turn) G.turn.nextUnavoidable = true;
}

/** Босс берёт карту в начале хода, когда он последний по очкам (Танатыч); сам добор — в game.ts (очки считает движок) */
export function catchUpDraw(p: Player): boolean {
  return !!bossPassive(p)?.catchUpDraw;
}

/** В начале хода босса враг-лидер получает вялую палочку (Король Похоти); исполняет game.ts */
export function limpToLeader(p: Player): boolean {
  return !!bossPassive(p)?.limpToLeader;
}

/** Вялые палочки босса не штрафуют (Король Похоти) */
export function limpNoPenalty(p: Player): boolean {
  return !!bossPassive(p)?.limpNoPenalty;
}

export function extraDraw(G: Game, p: Player): number {
  return traitPassive(p)?.extraDraw?.(G, p) ?? 0;
}

/**
 * Куда можно положить полученную карту (кроме сброса); варианты с одинаковым id схлопываются.
 * bought — куплена не с барахолки, но для Зада в будущее считается барахолкой: шальная магия из стопки
 * (с v12, chaosToHand) и свой фамильяр из-под планшета (с v14, familiarToHand)
 */
export function gainDestinations(G: Game, p: Player, gained: CardInst, fromMarket: boolean, bought = false): PromptOption[] {
  const opts: PromptOption[] = [];
  if (G.turn && G.turn.player === p.id && G.turn.gainToHand && (fromMarket || bought)) opts.push({ id: 'hand', label: 'На руку (Зад в будущее)' });
  const t = traitPassive(p);
  if (t?.gainDestinations) opts.push(...t.gainDestinations(G, p, gained, fromMarket));
  for (const { c, ps } of permanentPassives(p)) if (ps.gainDestinations) opts.push(...ps.gainDestinations(G, p, gained, fromMarket, c));
  const uniq: PromptOption[] = [];
  for (const o of opts) if (!uniq.some((u) => u.id === o.id)) uniq.push(o);
  return uniq;
}

/** Триггеры «получив карту» */
export function* onGain(G: Game, p: Player, gained: CardInst): Eff {
  T.PASSIVES[gained.cid]?.onGained?.(G, p, gained);
  const t = traitPassive(p);
  if (t?.onGain) yield* t.onGain(G, p, gained);
}

export function discount(p: Player, def: CardDef): number {
  const b = bossPassive(p);
  return (traitPassive(p)?.discount?.(def) ?? 0) + (b?.discount?.(def) ?? 0) + (b?.wandDiscount && def.tags.includes('wand') ? b.wandDiscount : 0);
}

export function familiarsAreLegends(p: Player): boolean {
  return !!traitPassive(p)?.familiarsAreLegends;
}

/** Легенда «для владельца»: жетон «фамильяры считаются легендами» — там, где это выгодно */
export function isLegendFor(p: Player | null, c: CardInst): boolean {
  if (card(c).legend) return true;
  return !!p && familiarsAreLegends(p) && card(c).type === 'фамильяр';
}

export function noPrizeBonus(p: Player): boolean {
  return ddkPassives(p).some(({ ps }) => ps.noPrizeBonus);
}

export function limpPositive(_p: Player, owned: readonly CardInst[]): boolean {
  return owned.some((c) => T.PASSIVES[c.cid]?.limpPositive);
}

/** Карта под контролем относится к типу (Кондуктор — ко всем) */
export function typeMatches(c: CardInst, type: CardDef['type']): boolean {
  return card(c).type === type || hasTag(c, 'allTypes');
}

/** Уничтожен ли жетон в конце игры */
export function ddkDestroyedAtEnd(G: Game, p: Player, def: DdkDef): boolean {
  return !!T.DDK_PASSIVES[def.key]?.destroyedAtEnd?.(G, p, def);
}

/** Условные строки очков: карты владельца, жетон свойства, ЖДК */
export function endGameLines(G: Game, p: Player, ctx: EndGameCtx): ScoreLine[] {
  const lines: ScoreLine[] = [];
  for (const c of ctx.owned) {
    const l = T.PASSIVES[c.cid]?.endGameVP?.(G, p, ctx, c);
    if (l) lines.push(l);
  }
  const t = traitPassive(p);
  const tl = t?.endGameVP?.(G, p, ctx);
  if (tl) lines.push(tl);
  const bl = bossPassive(p)?.endGameVP?.(G, p, ctx);
  if (bl) lines.push(bl);
  const seenKeys = new Set<string>();
  for (const { def, ps } of ddkPassives(p)) {
    if (seenKeys.has(def.key)) continue; // один и тот же текст на двух жетонах — эффект один
    seenKeys.add(def.key);
    const l = ps.endGameVP?.(G, p, ctx, def);
    if (l) lines.push(l);
  }
  return lines;
}

/** Активируемые жетоны игрока (свойство ☀ и ЖДК) с их доступностью сейчас */
export function tokenActivations(G: Game, p: Player): TokenActivation[] {
  const res: TokenActivation[] = [];
  const t = traitPassive(p);
  const traitUsed = G.turn ? G.turn.activated.includes('trait') : true;
  if (t?.activation && !traitUsed) {
    const a = t.activation;
    res.push({ id: a.id, label: a.label, enabled: a.condition(G, p), hint: a.hint, ...(a.auto ? { auto: true } : {}) });
  }
  const seen = new Set<string>();
  for (const { ps } of ddkPassives(p)) {
    const a = ps.activation;
    if (!a || seen.has(a.id)) continue;
    seen.add(a.id);
    res.push({ id: a.id, label: a.label, enabled: a.condition(G, p), hint: a.hint, ...(a.auto ? { auto: true } : {}) });
  }
  return res;
}

/** Эффект активации жетона по id (или null) */
export function tokenActivationRun(p: Player, id: string): ((G: Game, p: Player) => Eff) | null {
  const t = traitPassive(p);
  if (t?.activation && t.activation.id === id) return t.activation.run;
  for (const { ps } of ddkPassives(p)) if (ps.activation && ps.activation.id === id) return ps.activation.run;
  return null;
}

/** Жизни после смерти / в начале партии */
export function baseLife(G: Game, p: Player, fallback: number): number {
  return traitPassive(p)?.life?.(G) ?? bossPassive(p)?.life ?? fallback;
}
